"use client";

import { useCallback, useEffect, useState } from "react";

/** Extra frames fetch only when the card is actually on screen. */
const VIEW_MARGIN = "80px 0px";

/**
 * True while the card is in (or just about to enter) the viewport.
 * Posters do not use this — they load through a normal <img>.
 */
export function useCatalogViewport() {
  const [node, setNode] = useState<Element | null>(null);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  const ref = useCallback((el: Element | null) => {
    setNode(el);
  }, []);

  useEffect(() => {
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: VIEW_MARGIN }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  useEffect(() => {
    const onVis = () => setPageVisible(document.visibilityState === "visible");
    onVis();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return {
    ref,
    inView: inView && pageVisible && !reducedMotion,
  };
}
